import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Zoom,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DEFAULT_CANCELLATION_MESSAGE } from "../../constants/defaultCancellationMessage";
import { getMarkdownContentSx } from "../../utils/markdownContentSx";
import { HOME_PREOPEN_CARD_MAX_WIDTH_PX } from "./homeConstants";

type HomeCancellationAnnouncementProps = {
  message?: string | null;
};

/**
 * Bandeau d'information affiché lorsque le tournoi est annulé.
 * Le contenu est paramétrable en Markdown depuis l'admin.
 */
export const HomeCancellationAnnouncement: React.FC<
  HomeCancellationAnnouncementProps
> = ({ message }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentColor = theme.palette.error.main;
  const content = message?.trim() || DEFAULT_CANCELLATION_MESSAGE;

  return (
    <Zoom in>
      <Paper
        elevation={0}
        component="section"
        aria-labelledby="home-cancellation-title"
        sx={{
          width: "100%",
          maxWidth: HOME_PREOPEN_CARD_MAX_WIDTH_PX,
          mx: "auto",
          mt: { xs: 0.5, sm: 1 },
          mb: { xs: 2.5, sm: 3 },
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 3, sm: 3.5 },
          borderRadius: { xs: 3, md: 4 },
          textAlign: "left",
          border: `1px solid ${alpha(accentColor, isDark ? 0.4 : 0.35)}`,
          background: isDark
            ? `linear-gradient(
                165deg,
                ${alpha(theme.palette.error.dark, 0.35)} 0%,
                #1a0f12 48%,
                ${alpha(theme.palette.error.main, 0.18)} 100%
              )`
            : `linear-gradient(
                165deg,
                ${alpha(theme.palette.error.main, 0.06)} 0%,
                ${theme.palette.background.paper} 62%
              )`,
          boxShadow: isDark
            ? `0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`
            : "0 8px 32px rgba(180, 40, 40, 0.1)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack spacing={2} sx={{ position: "relative" }}>
          <Typography
            id="home-cancellation-title"
            variant="subtitle1"
            component="h2"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              color: isDark ? "grey.200" : "text.secondary",
              lineHeight: 1.4,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Informations aux participants
          </Typography>

          <Box
            sx={{
              color: isDark ? "grey.200" : "text.primary",
              ...getMarkdownContentSx(theme),
              "& p:last-child": { mb: 0 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </Box>
        </Stack>
      </Paper>
    </Zoom>
  );
};
